import { MockedRequest, ResponseResolver, restContext, RestRequest } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import { MONTHLY_APPLICATION_STATUS } from '../../constants/monthlyApplications';
import { choiceRandom } from '@/utils/useHelper';
import dayjs from 'dayjs';
import {
  MonthlyApplication,
  MonthlyApplicationDetail,
  MonthlyApplications,
  ApplicationStatus,
} from '../types/monthlyApplication';
import { ApplicationSuggestions } from '../types/application';
import { MonthlyParkingResponse } from '../types/monthlyParking';
import {
  Notice,
  NoticeDetailResponse,
  NoticesResponse,
  NoticeContentData,
} from '../types/notice';
import {
  MONTHLY_NOTICE_MESSAGES,
  MONTHLY_NOTICE_TYPE,
} from '../../constants/notice';
import { ResubmissionResponse, ResubmissionType } from '../types/resubmission';
import {
  MonthlyPayment,
  MonthlyPaymentsResponse,
  MonthlyPaymentUsage,
  PaymentConfirmResponse,
  PaymentGetResultResponse,
} from '../types/payment';
import { MONTHLY_PAYMENT_USAGE } from '../../constants/payment';
import { PersonalApplicantResponse } from '../types/applicant';
import { ACCOUNT_TYPES } from '~/features/user/constants';

/**
 * 月極駐車場詳細を取得
 */
const getMonthlyParking: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const parkingId = Number(req.params.parkingId) || 1;

  // テストケースに対応したデータ生成
  const getMonthlyTestData = (id: number) => {
    switch (id) {
      case 4: // 月極: 貸出区画がない駐車場
        return {
          hasAvailableGarage: false,
          canAcceptApplications: false,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】月極:貸出区画なし',
        };
      case 5: // 月極: 全ての貸出区画が公開日前の駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: false,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】月極:全区画公開日前',
        };
      case 6: // 月極: 公開日を過ぎた貸出区画がひとつでもある駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】月極:仮申込可能',
        };
      case 7: // 詳細: オープン前の駐車場（公開予定画面表示）
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】詳細:オープン前（公開予定）',
        };
      case 8: // 詳細: オープン済み駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】詳細:オープン済み',
        };
      case 9: // 詳細: 時間貸しできない駐車場
        return {
          hasAvailableGarage: false,
          canAcceptApplications: false,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】詳細:時間貸しできない',
        };
      case 10: // 月極詳細: 貸出区画がない駐車場
        return {
          hasAvailableGarage: false,
          canAcceptApplications: false,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極詳細:契約できない',
        };
      case 11: // 月極詳細: 全ての貸出区画が公開日前の駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: false,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極詳細:全区画公開日前',
        };
      case 12: // 月極詳細: 公開日を過ぎた貸出区画がひとつでもある駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極詳細:仮申込可能',
        };
      default:
        return {
          hasAvailableGarage: !!choiceRandom([0, 1]),
          canAcceptApplications: parkingId !== 31,
          isOpened: parkingId % 3 !== 0,
          publishDateTime:
            parkingId % 3 === 0
              ? '2025-12-31T09:00:00+09:00'
              : '2024-01-01T09:00:00+09:00',
          name: `テスト駐車場 ${parkingId}`,
        };
    }
  };

  const testData = getMonthlyTestData(parkingId);
  const hasAvailableGarage = testData.hasAvailableGarage;
  const canAcceptApplications = testData.canAcceptApplications;

  // 貸出区画リストを生成
  const rentalGarages = hasAvailableGarage
    ? [
        {
          contractAmount: 10000,
          contractAmountWithTax: 11000,
          depositAmount: 30000,
          monthlyAmount: 20000,
          monthlyAmountWithTax: 22000,
          type: '普通',
          content: '普通車用',
          isDummy: choiceRandom([true, false]),
        },
        {
          contractAmount: 8000,
          contractAmountWithTax: 8800,
          depositAmount: 25000,
          monthlyAmount: 15000,
          monthlyAmountWithTax: 16500,
          type: 'バイク',
          content: null,
          isDummy: false,
        },
        {
          contractAmount: 12000,
          contractAmountWithTax: 13200,
          depositAmount: 35000,
          monthlyAmount: 25000,
          monthlyAmountWithTax: 27500,
          type: '特殊車両',
          content: '大型',
          isDummy: false,
        },
        {
          contractAmount: 12000,
          contractAmountWithTax: 13200,
          depositAmount: 35000,
          monthlyAmount: 25000,
          monthlyAmountWithTax: 27500,
          type: '普通',
          content: '普通車用',
          isDummy: false,
        },
      ]
    : [];

  // 貸出区画の集計を計算
  const rentalGaragesSummary: MonthlyParkingResponse['rentalGaragesSummary'] = {
    maxContractAmount: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.contractAmount))
      : 0,
    maxContractAmountWithTax: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.contractAmountWithTax))
      : 0,
    maxDepositAmount: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.depositAmount))
      : 0,
    maxMonthlyAmount: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.monthlyAmount))
      : 0,
    maxMonthlyAmountWithTax: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.monthlyAmountWithTax))
      : 0,
    minContractAmount: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.contractAmount))
      : 0,
    minContractAmountWithTax: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.contractAmountWithTax))
      : 0,
    minDepositAmount: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.depositAmount))
      : 0,
    minMonthlyAmount: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.monthlyAmount))
      : 0,
    minMonthlyAmountWithTax: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.monthlyAmountWithTax))
      : 0,
  };

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyParkingResponse>({
      garageImageUrl: 'https://picsum.photos/700/500',
      hasApplied: Math.random() > 0.5, // ランダムに申込済みフラグを設定
      openTime: '24時間',
      parkingAddress: '東京都渋谷区道玄坂1-2-3',
      parkingId,
      parkingName: testData.name,
      // テストケース対応
      isOpened: testData.isOpened,
      publishDateTime: testData.publishDateTime,
      hasAvailableGarage,
      rentalGarages,
      rentalGaragesSummary,
      parkingLatitude: 35.710063,
      parkingLongitude: 139.8107,
      isDummy: rentalGarages.some((garage) => garage.isDummy),
      canAcceptApplications,
      applicationSuspensionPeriod: canAcceptApplications
        ? null
        : {
            startDate: '2025-01-01',
            endDate: '2025-01-31',
            resumeDate: '2025-02-01',
            reason:
              '誠に勝手ながら夏期休業に伴い仮申込の受付を一時停止しております。10月1日より受付再開いたします。',
          },
    }),
  );
};

/**
 * 全ての契約ステータスのモックを生成
 */
const generateMockApplications = (
  page: number,
  limit: number,
): MonthlyApplications['applications'] => {
  const currentDateString = new Date().toString();

  const applications = (
    Object.keys(MONTHLY_APPLICATION_STATUS) as ApplicationStatus[]
  ).map<MonthlyApplication>((statusKey, i) => ({
    id: i + 1,
    status: statusKey as keyof typeof MONTHLY_APPLICATION_STATUS,
    statusLabel:
      MONTHLY_APPLICATION_STATUS[
        statusKey as keyof typeof MONTHLY_APPLICATION_STATUS
      ].label,
    parkingId: 100 + i,
    parkingName: `テスト駐車場 ${i}`,
    garageNumber: i,
    cardId: '7L6GMKZC043DG8W4FRMZF45LX2',
    closeDate: '2025-05-08',
    initialPayment: {
      contractAmount: 10000,
      depositAmount: 10000,
      initialMonthlyAmount: 10000,
      monthlyAmount: 10000,
      proratedAmount: 10000,
      totalAmount: 10000,
    },
    parkingAddress: '東京都新宿区',
    applicantLastName: '田中',
    applicantFirstName: '太郎',
    applicantLastNameKana: 'タナカ',
    applicantFirstNameKana: 'タロウ',
    renewalPayment: {
      dueDate: '2025-05-08',
      renewalAmount: 10000,
    },
    scheduledStartDate: '2025-05-08',
    unpaidPayment: {
      dueDate: '2025-05-08',
      targetMonth: '5',
      unpaidAmount: 10000,
    },
    appliedAt: currentDateString,
    requestedStartDate:
      statusKey === MONTHLY_APPLICATION_STATUS.APPLICATION_PENDING.ID
        ? '最短希望'
        : currentDateString,
    contractStartDate: currentDateString,
    contractUpdateDate: currentDateString,
    applicationDueDate: currentDateString,
    contractEndDate: currentDateString,
    contractSignedAt: currentDateString,
    rejectedAt: currentDateString,
    closedAt: currentDateString,
    canPayInitialPayment: (
      [
        MONTHLY_APPLICATION_STATUS.APPLICATION_INITIAL_PAYMENT_PENDING.ID,
      ] as ApplicationStatus[]
    ).includes(statusKey),
    canUpdateContract: false,
    canUpdatePaymentMethod: false,
    canSubmitApplication: false,
    canCancelContract: false,
    canCancelApplication: false,
    hasResubmissionRequests: [
      'PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED',
      'APPLICATION_RESUBMISSION_REQUESTED',
    ].includes(statusKey),
    hasUnsubmittedImages: true,
    hasPaymentIssues: true,
    canRenewContract:
      (
        [
          MONTHLY_APPLICATION_STATUS.CONTRACT_IN_PROGRESS.ID,
        ] as ApplicationStatus[]
      ).includes(statusKey) && choiceRandom([true, false]),
    requestedCloseDate: currentDateString,
    scheduledCloseDate: currentDateString,
  }));

  const offset = (page - 1) * limit;

  return applications.slice(offset, offset + limit);
};

// 貸出一覧を取得する
const getMonthlyApplications: ResponseResolver<
  MockedRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const url = new URL(req.url);
  const page = Number(url.searchParams.get('page')) || 1;
  const limit = Number(url.searchParams.get('limit')) || 10;

  const totalItems = 50;
  const totalPages = Math.ceil(totalItems / limit);

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyApplications>({
      applications: generateMockApplications(page, limit),
      pagination: {
        total: totalItems,
        perPage: limit,
        currentPage: page,
        lastPage: totalPages,
        firstPageUrl: '',
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
      },
    }),
  );
};

// 貸出契約詳細を取得する
const getMonthlyApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  if (!applicationId) return delayedResponse(ctx.status(404));

  const currentDateString = new Date().toString();

  /** applicationId に基づいた契約ステータス */
  const status =
    MONTHLY_APPLICATION_STATUS[
      Object.keys(MONTHLY_APPLICATION_STATUS)[
        applicationId - 1
      ] as ApplicationStatus
    ];

  const initialPayment = {
    canPay: true,
    contractAmount: 10000,
    depositAmount: 50000,
    initialMonthlyAmount: 10000,
    monthlyAmount: 20000,
    proratedAmount: 10000,
    proratedDays: 15,
    targetMonth: dayjs().format('YYYY-MM'),
    totalAmount: 100000,
  };

  const renewalPayment = {
    canPay: true,
    dueDate: dayjs().add(11, 'month').toString(),
    renewalAmount: 10000,
  };

  const unpaidPayment = {
    canPay: true,
    dueDate: dayjs().add(10, 'day').toString(),
    targetMonth: dayjs().format('YYYY-MM'),
    unpaidAmount: 20000,
  };

  const application: MonthlyApplicationDetail = {
    applicationDueDate: dayjs().add(14, 'day').toString(),
    appliedAt: currentDateString,
    cardId: '7L6GMKZC043DG8W4FRMZF45LX2',
    closedAt: dayjs().subtract(1, 'day').toString(),
    closeDate: dayjs().add(10, 'day').toString(),
    contractEndDate: dayjs().add(1, 'year').toString(),
    contractSignedAt: dayjs().subtract(5, 'day').toString(),
    contractStartDate: dayjs().subtract(10, 'day').toString(),
    garageNumber: 101,
    id: applicationId,
    parkingAddress: '東京都渋谷区道玄坂1-2-3',
    parkingId: 5678,
    parkingName: '渋谷パーキング',
    rejectedAt: dayjs().subtract(2, 'day').toString(),
    monthlyAmount: 50000,
    initialPayment,
    renewalPayment,
    unpaidPayment,
    requestedCloseDate: dayjs().subtract(3, 'day').toString(),
    requestedStartDate: dayjs().subtract(7, 'day').toString(),
    scheduledStartDate: dayjs().toString(),
    status: status.ID,
    statusLabel: status.label,
    cancellationDateRange: {
      minDate: '2025-05-01',
      maxDate: '2025-10-01',
    },
  };

  if (!application)
    return delayedResponse(
      ctx.status(400),
      ctx.json({
        messages: ['本申込が存在しません'],
        code: 100,
      }),
    );

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyApplicationDetail>(application),
  );
};

// 仮申込の入力補完を取得する
const getPreApplicationSuggestions: ResponseResolver<
  RestRequest,
  typeof restContext
> = (req, res, ctx) => {
  const parkingId = req.url.searchParams.get('parkingId');
  const applicationId = req.url.searchParams.get('applicationId');
  return delayedResponse(
    ctx.status(200),
    ctx.json({
      member: {
        id: 1,
        userId: 101,
        lastName: '山田',
        firstName: '太郎',
        lastNameKana: 'ヤマダ',
        firstNameKana: 'タロウ',
        phoneNumber: ['080', '1234', '5678'],
        zipCode: '123-4567',
        gender: 1,
        birthday: '1990-01-01T00:00:00Z',
        prefecture: '東京都',
        city: '渋谷区',
        streetAddress: '1-2-3',
        building: '渋谷ハイツ',
        emails: ['yamada.taro@example.com', 'yamada.taro2@example.com'],
      },
      corporateAccount: {
        id: 10,
        name: 'ナビパーク株式会社',
        nameKana: 'ナビパークカブシキガイシャ',
        zipCode: '150-0002',
        addressRegion: '東京都',
        addressLocality: '渋谷区',
        addressStreet: '渋谷2-21-1',
        addressBuilding: '渋谷スクランブルスクエア',
        phoneNumber: ['03', '1234', '5678'],
        emails: ['corp@example.com', 'corp2@example.com'],
      },
      selectableStartDates: ['2023-01-01', '2023-01-15', '2023-02-01'],
    }),
  );
};

// 本申込の入力補完を取得する
const getApplicationSuggestions: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);

  const usagePurpose = choiceRandom(['PERSONAL', 'CORPORATION'] as const);

  return delayedResponse(
    ctx.status(200),
    ctx.json<ApplicationSuggestions>({
      contractStartDate: '2025-05-01',
      dueDate: '2025-04-30T23:59:59',
      garageImageUrl: 'https://picsum.photos/700/500',
      genderMapping: [
        { enum: 'MAN', label: '男' },
        { enum: 'WOMAN', label: '女' },
      ],
      parkingAddress: '東京都渋谷区道玄坂1-2-3',
      parkingName: '渋谷パーキング',
      selectableGarages: [
        {
          contractAmount: 10000,
          depositAmount: 50000,
          garageId: 1,
          garageNumber: 101,
          monthlyAmount: 20000,
          renewalAmount: 5000,
          content: null,
          type: 'STANDARD',
          typeLabel: '普通',
        },
        {
          contractAmount: 15000,
          depositAmount: 60000,
          garageId: 2,
          garageNumber: 102,
          monthlyAmount: 25000,
          renewalAmount: 6000,
          content: '屋根付き',
          type: 'SPECIAL',
          typeLabel: '特殊区画',
        },
      ],
      usagePurpose,
      usagePurposeLabel: usagePurpose === 'PERSONAL' ? '個人利用' : '法人利用',
      workplaceIndustryCategoryMapping: [
        { enum: 'CONSTRUCTION', label: '建設業' },
      ],
      overrideMonthlyAmount: choiceRandom([null, 55000]),
      jobTypeMapping: [
        { enum: 'COMPANY_OFFICER', label: '会社役員' },
        { enum: 'COMPANY_EMPLOYEE', label: '会社員' },
        { enum: 'SELF_EMPLOY', label: '自営業' },
        { enum: 'PART_TIME_JOB', label: 'パート・アルバイト' },
        { enum: 'STUDENT', label: '学生' },
        { enum: 'HOMEWORKER_OTHER', label: '家事従事者/その他' },
        { enum: 'UNEMPLOYED', label: '無職' },
      ],
    }),
  );
};

// 仮申込申請
const postPreApplication: ResponseResolver<
  MockedRequest,
  typeof restContext
> = async (req, res, ctx) => {
  if (choiceRandom([true, false])) {
    return delayedResponse(ctx.status(201), ctx.json({ applicationId: 1 }));
  } else {
    return delayedResponse(
      ctx.status(400),
      ctx.json({
        messages: ['エラーメッセージ'],
        code: 213,
      }),
    );
  }
};

// 本申込申請
const postApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);

  return delayedResponse(ctx.status(204));
};

// ログインユーザー宛の通知一覧を取得
const getNotices: ResponseResolver<RestRequest, typeof restContext> = async (
  req,
  res,
  ctx,
) => {
  const pinned = req.url.searchParams.get('pinned');
  const viewed = req.url.searchParams.get('viewed');
  const page = Number(req.url.searchParams.get('page')) || 1;
  const limit = Number(req.url.searchParams.get('limit')) || 1;

  const totalItems = MONTHLY_NOTICE_MESSAGES.length;
  const totalPages = Math.ceil(totalItems / limit);

  const generatedNotices = MONTHLY_NOTICE_MESSAGES.map<Notice>(
    (message, index) => ({
      id: index,
      type: message.TYPE,
      isPinned: true,
      publishedAt: new Date().toString(),
      viewedAt: choiceRandom([null, new Date().toString()]),
    }),
  );

  return delayedResponse(
    ctx.status(200),
    ctx.json<NoticesResponse>({
      notices: generatedNotices.slice(
        limit * (page - 1),
        limit * (page - 1) + limit,
      ),
      pagination: {
        total: totalItems,
        perPage: limit,
        currentPage: page,
        lastPage: totalPages,
        firstPageUrl: '',
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
      },
    }),
  );
};

/**
 * 通知詳細を取得する
 */
const getNoticeDetail: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const noticeId = req.params.noticeId;
  const noticeType = MONTHLY_NOTICE_MESSAGES[Number(noticeId)].TYPE;

  // 全ての通知で共通のデータを生成
  const contentData: NoticeContentData = {
    parkingId: 5678,
    parkingName: '渋谷パーキング',
    applicationId:
      noticeType === MONTHLY_NOTICE_TYPE.UNSUBMITTED_IMAGE_SUBMISSION_REQUEST
        ? 8
        : 17,
    billingAmount: 22000,
    billingCardNumber: '************1234',
    billingDueDate: '2025-05-31T23:59:59',
    billingId: 9876,
    billingTargetMonth: '2025-05-15T14:30:45',
    cancellationRequestedAt: '2025-05-15T14:30:45',
    cancellationRequestId: 5678,
    contractCloseDate: '2025-06-30',
    contractUpdatedDate: '2025-06-01',
    previousGarageNumber: 105,
    previousMonthlyAmount: 22000,
    contractEndDate: '2025-08-31',
    contractRenewalDueDate: '2025-06-30',
    contractGarageNumber: 201,
    contractMonthlyAmount: 25000,
    contractRenewalAmount: 10000,
    unsubmittedImageNames: ['車検証', '運転免許証裏面', '車両側面写真'],
    billingPaidAt: '2025-05-20T10:15:30',
    contractRenewalAmountWithTax: 2000,
    notSubmittedImages: [],
  };

  return delayedResponse(
    ctx.status(200),
    ctx.json<NoticeDetailResponse>({
      id: Number(noticeId),
      type: noticeType,
      isPinned: true,
      publishedAt: new Date().toString(),
      viewedAt: null,
      data: contentData,
    }),
  );
};

/**
 * 再提出依頼を取得
 */
const getResubmission: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  const { getAccountType } = useCognitoAuth();
  const accountType = await getAccountType();

  /** applicationId に基づいた契約ステータス */
  const status =
    MONTHLY_APPLICATION_STATUS[
      Object.keys(MONTHLY_APPLICATION_STATUS)[
        applicationId - 1
      ] as ApplicationStatus
    ];

  let resubmissionTypes: ResubmissionType[];
  let comment: string;

  // accountTypeとstatusの組み合わせで条件分岐
  if (accountType === ACCOUNT_TYPES.PERSONAL) {
    switch (status?.ID) {
      case MONTHLY_APPLICATION_STATUS
        .PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 個人仮申込の再提出
        resubmissionTypes = [
          'EMAIL',
          'VEHICLE_OWNERSHIP',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'LICENSE_FRONT_IMAGE',
          'LICENSE_BACK_IMAGE',
          'CAR_INSPECTION_IMAGE',
        ];
        comment =
          'これは個人仮申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 個人本申込の再提出
        resubmissionTypes = [
          'GENDER',
          'BIRTHDAY',
          'ZIP_CODE',
          'ADDRESS',
          'LANDLINE_PHONE_NUMBER',
          'MOBILE_PHONE_NUMBER',
          'JOB_TYPE',
          'WORKPLACE_COMPANY_NAME',
          'WORKPLACE_COMPANY_NAME_KANA',
          'WORKPLACE_ZIP_CODE',
          'WORKPLACE_ADDRESS',
          'WORKPLACE_PHONE_NUMBER',
          'WORKPLACE_INDUSTRY_CATEGORY',
          'ANNUAL_INCOME',
          'EMERGENCY_CONTACT_NAME',
          'EMERGENCY_CONTACT_NAME_KANA',
          'EMERGENCY_CONTACT_PHONE_NUMBER',
          'EMERGENCY_CONTACT_RELATIONSHIP',
        ];
        comment =
          '個人本申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.CONTRACT_UPDATE_RESUBMISSION_REQUESTED.ID:
        // 個人契約内容の更新
        resubmissionTypes = [
          'EMAIL',
          'VEHICLE_OWNERSHIP',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'LICENSE_FRONT_IMAGE',
          'LICENSE_BACK_IMAGE',
          'CAR_INSPECTION_IMAGE',
          'GENDER',
          'BIRTHDAY',
          'ZIP_CODE',
          'ADDRESS',
          'LANDLINE_PHONE_NUMBER',
          'MOBILE_PHONE_NUMBER',
          'JOB_TYPE',
          'WORKPLACE_COMPANY_NAME',
          'WORKPLACE_COMPANY_NAME_KANA',
          'WORKPLACE_ZIP_CODE',
          'WORKPLACE_ADDRESS',
          'WORKPLACE_PHONE_NUMBER',
          'WORKPLACE_INDUSTRY_CATEGORY',
          'ANNUAL_INCOME',
          'EMERGENCY_CONTACT_NAME',
          'EMERGENCY_CONTACT_NAME_KANA',
          'EMERGENCY_CONTACT_PHONE_NUMBER',
          'EMERGENCY_CONTACT_RELATIONSHIP',
          'LAST_NAME',
          'FIRST_NAME',
          'LAST_NAME_KANA',
          'FIRST_NAME_KANA',
        ];
        comment =
          'これは個人契約内容の更新の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      default:
        // デフォルトは個人仮申込の再提出
        resubmissionTypes = [];
        comment = '再提出ができない契約ステータスです。';
    }
  } else {
    // 法人アカウント
    switch (status?.ID) {
      case MONTHLY_APPLICATION_STATUS
        .PROVISIONAL_APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 法人仮申込の再提出
        resubmissionTypes = [
          'EMAIL',
          'CORPORATE_NAME',
          'CORPORATE_NAME_KANA',
          'HEADQUARTERS_ADDRESS',
          'USAGE_PURPOSE',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'PLANNED_VEHICLE_COLOR',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'COMPANY_REGISTRY_IMAGE',
          'CAR_INSPECTION_IMAGE',
        ];
        comment =
          'これは法人仮申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.APPLICATION_RESUBMISSION_REQUESTED.ID:
        // 法人本申込の再提出
        resubmissionTypes = [
          'HEADQUARTERS_PHONE_NUMBER',
          'INDUSTRY',
          'REPRESENTATIVE_NAME',
          'REPRESENTATIVE_NAME_KANA',
          'LISTING_STATUS',
          'DEPARTMENT_NAME',
          'CONTACT_PERSON_NAME',
          'CONTACT_PERSON_NAME_KANA',
          'DEPARTMENT_ADDRESS',
          'DEPARTMENT_PHONE_NUMBER',
        ];
        comment =
          'これは法人本申込の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      case MONTHLY_APPLICATION_STATUS.CONTRACT_UPDATE_RESUBMISSION_REQUESTED.ID:
        // 法人契約内容の更新
        resubmissionTypes = [
          'EMAIL',
          'CORPORATE_NAME',
          'CORPORATE_NAME_KANA',
          'HEADQUARTERS_ADDRESS',
          'USAGE_PURPOSE',
          'PARKED_VEHICLE_CATEGORY',
          'PARKED_VEHICLE_TYPE',
          'BIKE_DISPLACEMENT',
          'VEHICLE_NUMBER',
          'SCHEDULED_DELIVERY_DATE',
          'NO_VEHICLE_REASON',
          'PLANNED_VEHICLE_COLOR',
          'VEHICLE_FRONT_IMAGE',
          'VEHICLE_BACK_IMAGE',
          'VEHICLE_SIDE_IMAGE',
          'COMPANY_REGISTRY_IMAGE',
          'CAR_INSPECTION_IMAGE',
          'HEADQUARTERS_PHONE_NUMBER',
          'INDUSTRY',
          'REPRESENTATIVE_NAME',
          'REPRESENTATIVE_NAME_KANA',
          'LISTING_STATUS',
          'DEPARTMENT_NAME',
          'CONTACT_PERSON_NAME',
          'CONTACT_PERSON_NAME_KANA',
          'DEPARTMENT_ADDRESS',
          'DEPARTMENT_PHONE_NUMBER',
        ];
        comment =
          'これは法人契約内容の更新の再提出です。個人と法人はランダムに切り替えられます。';
        break;
      default:
        // デフォルトは法人仮申込の再提出
        resubmissionTypes = [];
        comment = '再提出ができない契約ステータスです。';
    }
  }

  return delayedResponse(
    ctx.status(200),
    ctx.json<ResubmissionResponse>({
      comment,
      resubmissionTypes,
    }),
  );
};

// 貸出の通知を閲覧済みにする
const markNoticeAsViewed: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  return delayedResponse(ctx.status(204));
};

/**
 * 申込者情報を更新する
 */
const putApplicant: ResponseResolver<RestRequest, typeof restContext> = async (
  req,
  res,
  ctx,
) => {
  const applicationId = Number(req.params.applicationId);
  return delayedResponse(ctx.status(204));
};

/**
 * 個人申込者情報のベースモックデータ
 * E2Eテストなどで再利用可能
 */
export const basePersonalApplicantResponse: PersonalApplicantResponse = {
  monthlyApplicationId: 110,
  parkingId: 451265,
  parkingName: '決済テスト用駐車場（モック1）',
  parkingAddress: '静岡県藤本市松本町',
  lastName: '後藤',
  firstName: '達也',
  lastNameKana: 'ゴトウ',
  firstNameKana: 'タツヤ',
  email: 't.goto+100@sonicmoov.com',
  gender: 'MAN',
  genderMapping: [
    {
      enum: 'MAN',
      label: '男',
    },
    {
      enum: 'WOMAN',
      label: '女',
    },
  ],
  birthday: '1904-10-09T00:00:00+09:00',
  zipCode: '1000001',
  addressRegion: '東京都千代田区千代田',
  addressDetail: 'テスト',
  landlinePhoneNumber: ['03', '1234', '5678'],
  mobilePhoneNumber: null,
  jobType: 'UNEMPLOYED',
  jobTypeMapping: [
    {
      enum: 'COMPANY_OFFICER',
      label: '会社役員',
    },
    {
      enum: 'COMPANY_EMPLOYEE',
      label: '会社員',
    },
    {
      enum: 'SELF_EMPLOY',
      label: '自営業',
    },
    {
      enum: 'PART_TIME_JOB',
      label: 'パート・アルバイト',
    },
    {
      enum: 'STUDENT',
      label: '学生',
    },
    {
      enum: 'HOMEWORKER_OTHER',
      label: '家事従事者/その他',
    },
  ],
  workplaceCompanyName: null,
  workplaceCompanyNameKana: null,
  workplaceZipCode: null,
  workplaceAddressRegion: null,
  workplaceAddressDetail: null,
  workplacePhoneNumber: null,
  workplaceIndustryCategory: null,
  workplaceIndustryCategoryMapping: [
    {
      enum: 'CONSTRUCTION',
      label: '建設業',
    },
    {
      enum: 'MANUFACTURING',
      label: '製造業',
    },
    {
      enum: 'ELECTRICITY_GAS',
      label: '電気・ガス',
    },
    {
      enum: 'TRANSPORTATION_COMMUNICATION',
      label: '運輸・通信業',
    },
    {
      enum: 'WHOLESALE_RETAIL_FOOD',
      label: '卸売・小売・飲食業',
    },
    {
      enum: 'FINANCE_INSURANCE',
      label: '金融・保険業',
    },
    {
      enum: 'REAL_ESTATE',
      label: '不動産業',
    },
    {
      enum: 'SERVICE',
      label: 'サービス業',
    },
    {
      enum: 'EDUCATION',
      label: '教育',
    },
    {
      enum: 'AGRICULTURE_FORESTRY_FISHERY',
      label: '農林水産業',
    },
    {
      enum: 'OTHER',
      label: 'その他',
    },
  ],
  annualIncome: 500,
  householdIncome: null,
  emergencyContactLastName: 'あ',
  emergencyContactFirstName: 'あ',
  emergencyContactLastNameKana: 'ア',
  emergencyContactFirstNameKana: 'ア',
  emergencyContactPhoneNumber: ['080', '1234', '5678'],
  emergencyContactRelationship: 'テスト',
  usagePurpose: 'PERSONAL',
  usagePurposeMapping: [
    {
      enum: 'PERSONAL',
      label: '個人利用',
    },
    {
      enum: 'CORPORATION',
      label: '法人利用',
    },
  ],
  vehicleOwnershipStatus: 'PENDING',
  vehicleOwnershipStatusMapping: [
    {
      enum: 'OWNED',
      label: '保有している',
    },
    {
      enum: 'NOT_OWNED',
      label: '保有していない',
    },
    {
      enum: 'PENDING',
      label: '未定',
    },
  ],
  parkedVehicleType: 'セダン',
  scheduledDeliveryDate: null,
  noVehicleReason: 'テスト',
  frontVehicleImageUrl: 'https://picsum.photos/700/500',
  backVehicleImageUrl: 'https://picsum.photos/700/500',
  sideVehicleImageUrl: 'https://picsum.photos/700/500',
  frontLicenseImageUrl: 'https://picsum.photos/700/500',
  backLicenseImageUrl: 'https://picsum.photos/700/500',
  carInspectionImageUrl: null,
  parkedVehicleCategory: 'AUTOMOBILE',
  bikeDisplacement: 'UNDER_125CC',
  vehicleNumber: {
    localName: '品川',
    classificationNumber: '123',
    hiragana: 'あ',
    serialNumber: '12-34',
  },
};

/**
 * 申込者情報取得ハンドラーを生成する
 * @param override レスポンスを上書きする（テストで使用）
 */
export const createGetApplicant = (
  override?: Partial<PersonalApplicantResponse>,
): ResponseResolver<RestRequest, typeof restContext> => {
  return async (_req, _res, ctx) => {
    return delayedResponse(
      ctx.status(200),
      ctx.json<PersonalApplicantResponse>({
        ...basePersonalApplicantResponse,
        ...override,
      }),
    );
  };
};

const putPaymentMethod: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  const body = await req.json();

  if (!body['cardId']) {
    return delayedResponse(
      ctx.status(404),
      ctx.json({
        messages: ['カードIDが必要です'],
        code: 100,
      }),
    );
  }
  return delayedResponse(ctx.status(204));
};

const postPaymentConfirm: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const body = await req.json();
  if (
    !body['applicationId'] ||
    !body['usage'] ||
    !body['totalAmount'] ||
    !body['paymentType']
  ) {
    return delayedResponse(
      ctx.status(404),
      ctx.json({
        messages: ['必須項目の入力値が不足しています'],
        code: 100,
      }),
    );
  }
  return delayedResponse(
    ctx.status(200),
    ctx.json<PaymentConfirmResponse>({
      addYumenaviPoint: 100,
      paymentAmountWithDiscount: 50000,
      useYumenaviPoint: 1000,
    }),
  );
};

const postPayment: ResponseResolver<RestRequest, typeof restContext> = async (
  req,
  res,
  ctx,
) => {
  const body = await req.json();
  if (
    !body['applicationId'] ||
    !body['usage'] ||
    !body['totalAmount'] ||
    !body['paymentType'] ||
    (body['paymentType'] === 1 && !body['cardId'])
  ) {
    return delayedResponse(
      ctx.status(404),
      ctx.json({
        messages: ['不正な入力値または項目が不足'],
        code: 100,
      }),
    );
  }
  if (body['paymentType'] === 1) {
    return delayedResponse(ctx.status(200), ctx.body('https://example.com/'));
  } else {
    const successRedirectionQuery = decodeURIComponent(
      body['successRedirectionQuery'],
    );
    const errorRedirectionQuery = decodeURIComponent(
      body['errorRedirectionQuery'],
    );
    const successUrl = new URL(
      successRedirectionQuery.replace('redirect_path=', ''),
      'http://localhost:3333',
    );
    const errorUrl = new URL(
      errorRedirectionQuery.replace('redirect_path=', ''),
      'http://localhost:3333',
    );
    successUrl.searchParams.set('paymentId', '1');
    errorUrl.searchParams.set('paymentId', '1');
    return delayedResponse(
      ctx.status(200),
      ctx.body(choiceRandom([successUrl.href, errorUrl.href])),
    );
  }
};

/**
 * 決済結果を取得
 */
const getPaymentResult: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const paymentId = Number(req.params.paymentId);
  if (!paymentId) {
    return delayedResponse(ctx.status(404));
  }
  return delayedResponse(
    ctx.status(200),
    ctx.json<PaymentGetResultResponse>({
      isCompleted: choiceRandom([true, false, false]),
      isSuccess: choiceRandom([true, false]),
      isCancelled: choiceRandom([true, false, false]),
    }),
  );
};

/**
 * 解約を申請
 */
const postCloseApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const body = await req.json();
  if (!body['closeDate']) {
    return delayedResponse(
      ctx.status(422),
      ctx.json({
        messages: ['エラー'],
      }),
    );
  }
  return delayedResponse(ctx.status(204));
};

const cancelApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  return delayedResponse(ctx.status(204));
};

const getContractDocument: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  if (!applicationId) {
    return delayedResponse(ctx.status(404));
  }
  return delayedResponse(
    ctx.status(200),
    ctx.body('https://example.com/contract.pdf'),
  );
};

const getMonthlyPayments: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const applicationId = Number(req.params.applicationId);
  if (!applicationId) {
    return delayedResponse(ctx.status(404));
  }
  const page = Number(req.url.searchParams.get('page')) || 1;
  const limit = Number(req.url.searchParams.get('per_page')) || 10;
  const totalItems = 50;
  const totalPages = Math.ceil(totalItems / limit);

  const payments: MonthlyPayment[] = Array.from({ length: limit }, (_, i) => ({
    id: i + 1 + (page - 1) * limit,
    applicationId,
    displayName: '初期費用',
    parkingId: 100 + i,
    parkingName: `テスト駐車場 ${i + 1}`,
    usage: choiceRandom<MonthlyPaymentUsage>([
      MONTHLY_PAYMENT_USAGE.MONTHLY_APPLICATION_INITIAL_PAYMENT,
      MONTHLY_PAYMENT_USAGE.MONTHLY_APPLICATION_RENEWAL_PAYMENT,
      MONTHLY_PAYMENT_USAGE.MONTHLY_BILLING_PAYMENT,
    ]),
    parkingAddress: '東京都渋谷区道玄坂1-2-3',
    isParkingDeleted: choiceRandom([true, false]),
    isReceiptExpired: choiceRandom([true, false]),
    paidAt: dayjs().subtract(i, 'day').toISOString(),
    usedAt: dayjs().subtract(i, 'day').toISOString(),
  }));

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyPaymentsResponse>({
      payments,
      pagination: {
        total: totalItems,
        perPage: limit,
        currentPage: page,
        lastPage: totalPages,
        firstPageUrl: `http://localhost:3333/monthly/payments?page=1&per_page=${limit}`,
        lastPageUrl: `http://localhost:3333/monthly/payments?page=${totalPages}&per_page=${limit}`,
        nextPageUrl: `http://localhost:3333/monthly/payments?page=${page + 1}&per_page=${limit}`,
        prevPageUrl: `http://localhost:3333/monthly/payments?page=${page - 1}&per_page=${limit}`,
      },
    }),
  );
};

const postCorporatePreApplication: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  return delayedResponse(
    ctx.status(201),
    ctx.json({
      applicationId: 10,
    }),
  );
};

const mockMonthly = {
  getMonthlyParking,
  getMonthlyApplication,
  getMonthlyApplications,
  getPreApplicationSuggestions,
  getApplicationSuggestions,
  postPreApplication,
  postApplication,
  getApplicant: createGetApplicant(),
  getNotices,
  getNoticeDetail,
  markNoticeAsViewed,
  getResubmission,
  putApplicant,
  putPaymentMethod,
  postPaymentConfirm,
  postPayment,
  postCloseApplication,
  cancelApplication,
  getPaymentResult,
  getContractDocument,
  getMonthlyPayments,
  postCorporatePreApplication,
};
export default mockMonthly;
